import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { params } from '@app/configurations';

import { FadeAnimation } from '@app/core/animations/fade.transition';
import { DEFAULTS, GlobalFiltersState } from '@app/core/store/states/global-filters/global-filters.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { map, Observable, switchMap } from 'rxjs';
import { SelectorTranslationTexts } from '../../models/selector.model';
import { CampaignSelectorService } from '../../services/campaign-selector.service';
import { getButtonLabel, getSelectorTooltip } from '../../services/global-filters.utils';
import { BaseGlobalSelectorComponent } from '../base-global-selector/base-global-selector.component';

@Component({
  standalone: false,
  selector: 'adspt-campaign-selector',
  templateUrl: './campaign-selector.component.html',
  styleUrls: ['./campaign-selector.component.scss'],
  animations: [FadeAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CampaignSelectorService],
})
export class CampaignSelectorComponent extends BaseGlobalSelectorComponent {
  heapInfo = {
    search: 'campaigns-global-search',
    switch: 'campaigns-global-filter-switch',
    item: 'campaigns-global-select',
    reset: 'campaigns-global-reset-filter',
    apply: 'campaigns-global-apply-filter',
  };
  translations$: Observable<SelectorTranslationTexts> = this.translate.get('global-filter.campaign-selector').pipe(
    map((values) => ({
      title: values.placeholder,
      search: values.search,
      all: values.all,
      active: values.active,
      selected: values.selected,
      notSelectable: values['not-selectable'],
      applyTooltip: values['apply-filters-tooltip'],
    })),
  );
  hasSrcQuery$ = this.store.select(GlobalFiltersState.hasCampaignQuery);
  hasDestQuery$ = this.store.select(GlobalFiltersState.hasDestCampaignQuery);

  readonly campaignsData$ = this.displayData$.pipe(
    switchMap((d) => {
      return this.translations$.pipe(
        map((translations) => {
          const defaultFilter = this.service.filterDefaults[this.service.defaultSubfiltersType];

          const customFilterTexts = {
            [DEFAULTS.campaignsDefaultFilter]: translations.active,
            EMPTY_STRING: translations.all,
          };

          const data = this.hasDestinationSelector ? [d.src, d.dest] : [d.src];
          return {
            ...d,
            labelType: this._label(!!defaultFilter),
            labels: {
              tooltip: getSelectorTooltip(data, this.hasDestinationSelector, customFilterTexts),
              buttonTexts: getButtonLabel(data, this.hasDestinationSelector, customFilterTexts),
            },
          };
        }),
      );
    }),
  );

  private _label(hasDefaultFilter: boolean) {
    const hasCQ =
      typeof this.store.selectSnapshot(RouterNavState.queryParam(params.GLOBAL_FILTER_CAMPAIGN_QUERY)) === 'string';
    const hasCDestQ =
      typeof this.store.selectSnapshot(RouterNavState.queryParam(params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY)) ===
      'string';

    switch (true) {
      case this.hasDestinationSelector && !hasCQ && !hasCDestQ && hasDefaultFilter:
      case !this.hasDestinationSelector && !hasCQ && hasDefaultFilter:
        return 'active';
      default:
        return 'all';
    }
  }

  constructor(
    private store: Store,
    service: CampaignSelectorService,
    translate: TranslateService,
    cd: ChangeDetectorRef,
  ) {
    super(service, translate, cd);
  }
}
